import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHelp} from '@/contexts/help-context'
import {getDocPage as getEnDocPage, getEntryForTestid as getEnEntry} from '@/locales/en/help/index'
import {getDocPage as getDeDocPage, getEntryForTestid as getDeEntry} from '@/locales/de/help/index'
import type {HelpRegistryEntry} from '@/locales/en/help/index'
import {RegistryHelpIconPortal} from './RegistryHelpIconPortal'

const ATTR_TESTID = 'data-testid'

interface LocaleRegistry {
    readonly getEntry: (testid: string) => HelpRegistryEntry | undefined
    readonly getDocPage: (entry: HelpRegistryEntry) => string | undefined
}

const REGISTRIES: Readonly<Record<string, LocaleRegistry>> = {
    en: {getEntry: getEnEntry, getDocPage: getEnDocPage},
    de: {getEntry: getDeEntry, getDocPage: getDeDocPage},
}

function pickRegistry(language: string): LocaleRegistry {
    const direct = REGISTRIES[language]
    if (direct) return direct
    const base = language.split('-')[0]
    return REGISTRIES[base] ?? REGISTRIES.en
}

interface RegistryTarget {
    element: Element
    testid: string
    entry: HelpRegistryEntry
    docPage: string | undefined
}

/**
 * HelpOverlay — mounted once at app root.
 *
 * Resolves help content by reading `data-testid` from each element in the DOM
 * and looking it up in the per-locale `helpRegistry` (generated TS modules).
 * Each entry carries pre-derived `text`, optional `docPage`, optional `anchor`,
 * and `aria-label`.
 */
export function HelpOverlay() {
    const {helpEnabled} = useHelp()
    const {i18n} = useTranslation()
    const language = i18n.language
    const registry = useMemo(() => pickRegistry(language), [language])

    const [targets, setTargets] = useState<RegistryTarget[]>([])
    const observerRef = useRef<MutationObserver | null>(null)

    const scan = useCallback(() => {
        const found: RegistryTarget[] = []

        const testidElements = document.querySelectorAll(`[${ATTR_TESTID}]`)
        testidElements.forEach(el => {
            const testid = el.getAttribute(ATTR_TESTID)
            if (!testid) return
            const entry = registry.getEntry(testid)
            if (!entry) return
            found.push({element: el, testid, entry, docPage: registry.getDocPage(entry)})
        })

        setTargets(found)
    }, [registry])

    useEffect(() => {
        if (!helpEnabled) {
            setTargets([])
            return
        }

        scan()

        observerRef.current = new MutationObserver(() => {
            requestAnimationFrame(scan)
        })

        observerRef.current.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: [ATTR_TESTID],
        })

        return () => {
            observerRef.current?.disconnect()
            observerRef.current = null
        }
    }, [helpEnabled, scan])

    if (!helpEnabled || targets.length === 0) return null

    return (
        <>
            {targets.map(target => (
                <RegistryHelpIconPortal
                    key={target.testid}
                    targetElement={target.element}
                    testid={target.testid}
                    entry={target.entry}
                    docPage={target.docPage}
                />
            ))}
        </>
    )
}
